import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/store'
import { selectPlaylistById } from '@store/slices/playlist.slice'
import React, { FC, useEffect, useState } from 'react'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import { Card } from 'react-bootstrap'
import { AppDatePicker } from '@components/shared/appDatePicker.component'
import { AppSelectComponent } from '@components/shared/appSelect.component'
import { toast } from 'react-toastify'
import {
  checkPlaylistForTime,
  getPlaylistForTime,
} from '@store/thunk/playlist.thunk'
import { enumToOptions } from '@shared/tools/enum.tools'
import { RadioStationsEnum } from '@shared/enums'
import {
  fetchAllVoices,
  selectAllVoices,
  selectVoiceLoading,
} from '@store/slices/voice.slice'
import AppButton from '@components/shared/appButton'
import ConsentModal from '@components/shared/consentModal.component'

const PlaylistFormComponent: FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { playlistId } = useParams()
  const playlist = useSelector((state: RootState) =>
    playlistId ? selectPlaylistById(state, playlistId) : null
  )
  const [date, setDate] = useState<Date>(new Date())
  const [selectStation, setSelectStation] = useState<string>(
    RadioStationsEnum.EXPRES
  )
  const [selectVoice, setSelectVoice] = useState<EntityId>()
  const [hourFrom, setHourFrom] = useState<string>('0')
  const [hourTo, setHourTo] = useState<string>('6')
  const [showConsentModal, setShowConsentModal] = useState(false)
  const [modalBody, setModalBody] = useState<string | React.ReactNode>('')
  const [followupUrl, setFollowupUrl] = useState<string | undefined>()

  const stationOptions = enumToOptions(RadioStationsEnum)
  const voices = useSelector(selectAllVoices)
  const voiceLoading = useSelector(selectVoiceLoading)
  const voiceOptions = voices.map((voice) => ({
    id: voice.id,
    label: voice.name,
  }))
  const hours: { id: EntityId; label: string }[] = Array.from(
    { length: 23 },
    (_, i) => i
  ).map((index) => {
    index += 1
    const value = index < 10 ? `0${index}:00` : `${index}:00`
    return { id: index.toString(), label: value }
  })

  useEffect(() => {
    if (playlist) {
      const playlistDate = new Date(playlist.fromTime)
      setDate(playlistDate)
    }
  }, [playlist])

  useEffect(() => {
    if (voices?.length == 0 && !voiceLoading) {
      dispatch(fetchAllVoices())
    }
    if (voices.length > 0 && !selectVoice) {
      const defaultVoice = voices.find((voice) => voice.isDefault)
      setSelectVoice(defaultVoice ? defaultVoice.id : voices[0].id)
    }
  }, [voices])

  const onSelectHourFromChange = (hour: EntityId | null) => {
    const hourToNum: number = parseInt(hourTo)
    const hourNum: number = parseInt(hour as string)
    if (hourNum === 0 && hourToNum === 24) {
      toast.warning('Playlist musí být v rámci jednoho dne')
    } else if (
      hour &&
      hourTo &&
      Number.parseInt(hourTo) > Number.parseInt(hour as string)
    ) {
      setHourFrom(hour as string)
    } else if (hourToNum < 23) {
      setHourTo((hourNum + 1).toString())
      setHourFrom(hour as string)
    } else {
      toast.warning('Playlist musí být v rámci jednoho dne')
    }
  }

  const onSelectHourToChange = (hour: EntityId | null) => {
    if (hour == '24' && hourFrom == '0') {
      toast.warning('Playlist musí být v rámci jednoho dne')
    } else if (
      hour &&
      hourFrom &&
      Number.parseInt(hourFrom) < Number.parseInt(hour as string)
    ) {
      setHourTo(hour as string)
    } else {
      toast.warning('Hodina DO musí být větší než hodina OD')
    }
  }
  const onSelectStationChange = (stationId: EntityId | null) => {
    if (stationId) {
      setSelectStation(stationId as string)
    } else {
      toast.warning('Stanice musí být vybrána')
    }
  }
  const onSelectVoiceChange = (voiceId: EntityId | null) => {
    if (voiceId) {
      setSelectVoice(voiceId as string)
    } else {
      toast.warning('Stanice musí být vybrána')
    }
  }

  const onDateChange = (newDate: Date | null) => {
    if (newDate) {
      setDate(newDate)
    } else {
      toast.warning('Den musí být vybrán')
    }
  }

  const getDateData = (): { fromTime: Date; toTime: Date } => {
    const fromTime = new Date(date.getTime())
    fromTime.setHours(Number.parseInt(hourFrom), 0, 0, 0)
    const toTime = new Date(date.getTime())
    toTime.setHours(Number.parseInt(hourTo), 0, 0, 0)
    return { fromTime, toTime }
  }

  const dispatchPlaylist = () => {
    const data = {
      selectVoice,
      ...getDateData(),
      voiceId: selectVoice!,
      stationId: selectStation!,
    }
    dispatch(getPlaylistForTime(data))
      .then(unwrapResult)
      .then((playlist) => {
        navigate(`/playlist/${playlist.id}` + (followupUrl ? followupUrl : ''))
      })
      .catch((e) => console.error(e))
  }

  const tryNewPlaylist = async (followupUrl?: string) => {
    setFollowupUrl(followupUrl)
    const data = {
      selectVoice,
      ...getDateData(),
      stationId: selectStation!,
    }

    const playlistIds = await dispatch(checkPlaylistForTime(data)).then(
      unwrapResult
    )

    const idLink = (playlistId: EntityId) => (
      <span
        key={`foundPlaylist-${playlistId}`}
        className={'link-primary'}
        onClick={(e) => {
          e.preventDefault()
          window.open(`${playlistId}`, '_blank')
        }}
        style={{ cursor: 'pointer' }}
      >
        {' '}
        {playlistId}{' '}
      </span>
    )

    const modalBody = (
      <span>
        Našli jsme kolidující playlist/y
        {playlistIds.map((playlistId) => idLink(playlistId))}. Opravdu chcete
        pokračovat?
      </span>
    )

    if (playlistIds.length > 0) {
      setModalBody(modalBody)
      setShowConsentModal(true)
    } else {
      return dispatchPlaylist()
    }
  }

  const onToDramaturgClick = () => {
    if (selectVoice) {
      return tryNewPlaylist('/outline')
    } else {
      toast.warning('Není vybrán hlas')
    }
  }

  const onGeneratePlaylistWithAudioClick = () => {
    toast.warning('Zatím není implementováno')
  }

  const onToPlaylistClick = () => {
    if (selectVoice) {
      return tryNewPlaylist()
    } else {
      toast.warning('Není vybrán hlas')
    }
  }

  return (
    <div className="my-2 w-50">
      <Card>
        <Card.Header>
          <h3>Stáhnout nový playlist</h3>
        </Card.Header>
        <Card.Body>
          <div className={'d-flex flex-column gap-2'}>
            <AppSelectComponent
              onSelectChange={onSelectStationChange}
              options={stationOptions}
              valueId={selectStation ? selectStation : undefined}
              placeholder={'Vyberte stanici'}
              className={'p-0 w-50'}
            ></AppSelectComponent>
            <AppSelectComponent
              onSelectChange={onSelectVoiceChange}
              options={voiceOptions}
              valueId={selectVoice ? selectVoice : undefined}
              placeholder={'Vyberte hlas'}
              className={'p-0 w-50'}
            ></AppSelectComponent>
            <div className={'d-flex gap-2'}>
              <AppDatePicker startDate={date} handleDateChange={onDateChange} />
              <AppSelectComponent
                onSelectChange={onSelectHourFromChange}
                options={[{ id: '0', label: '00:00' }, ...hours]}
                valueId={hourFrom ? hourFrom : undefined}
                placeholder={'Od'}
                className={'p-0'}
              ></AppSelectComponent>
              <AppSelectComponent
                onSelectChange={onSelectHourToChange}
                options={[...hours, { id: '24', label: '24:00' }]}
                valueId={hourTo ? hourTo : undefined}
                placeholder={'Do'}
                className={'p-0'}
              ></AppSelectComponent>
            </div>
            <div className={'my-2 d-flex gap-2'}>
              <AppButton
                buttonVariant={'primary'}
                onClick={onToDramaturgClick}
                buttonText={'Dramaturg'}
              />
              <AppButton
                buttonVariant={'primary'}
                onClick={onToPlaylistClick}
                buttonText={'Moderátor'}
              />
              <AppButton
                buttonVariant={'primary'}
                disabled={true}
                onClick={onGeneratePlaylistWithAudioClick}
                buttonText={'Revize audia'}
              />
            </div>
          </div>
        </Card.Body>
        <ConsentModal
          show={showConsentModal}
          header="Nový playlist překrývá jiné"
          body={modalBody}
          onAgree={dispatchPlaylist}
          onBack={() => {
            setShowConsentModal(false)
            setFollowupUrl(undefined)
          }}
          onHide={() => {
            setShowConsentModal(false)
            setFollowupUrl(undefined)
          }}
        />
      </Card>
    </div>
  )
}

export default PlaylistFormComponent
